// Cart-redirect "immediate" mode companion: shows a note on the gift line inside the UpCart flyout
// telling the customer the gift becomes free once their subtotal reaches the campaign threshold.
//
// Only acts when the active campaign (localStorage["fiera:qr_offer"]) is in `immediate` mode. The
// note is injected straight into UpCart's shadow DOM, so it must carry inline styles — theme
// stylesheets can't cross the shadow boundary. We bind with addEventListener (not
// window.upcartOnCartUpdated, which trynow-cart-features.liquid already owns) so we compose with the
// theme's other UpCart hooks instead of clobbering them.
(function () {
  'use strict';

  if (window.__qrGiftNoteBound) return;
  window.__qrGiftNoteBound = true;

  const QR_OFFER_STORAGE_KEY = 'fiera:qr_offer';
  const NOTE_CLASS = 'qr-gift-note';
  const UPCART_EVENTS = [
    'aftersell-upcart:public-events:cart-loaded',
    'aftersell-upcart:public-events:cart-updated',
    'aftersell-upcart:public-events:cart-opened',
  ];

  function readState() {
    try {
      const raw = localStorage.getItem(QR_OFFER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function getShadowRoot() {
    const host = document.querySelector('#upCart');
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  async function fetchCart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return response.ok ? response.json() : null;
    } catch (error) {
      return null;
    }
  }

  // Use the theme's shared money formatter (driven by window.moneyFormat = shop.money_format) so the
  // note's amount matches how prices render everywhere else. Mirrors assets/atc-summary.js.
  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') return Shopify.formatMoney(cents);
    return '$' + ((cents || 0) / 100).toFixed(2);
  }

  function makeNoteEl(text) {
    const el = document.createElement('div');
    el.className = NOTE_CLASS;
    el.textContent = text;
    el.style.cssText = [
      'margin-top:6px',
      'margin-bottom:6px',
      'padding:4px 8px',
      'font-size:12px',
      'line-height:1.3',
      'font-weight:600',
      'color:#8f549a',
      'background:#f6eef8',
      'border-radius:4px',
    ].join(';');
    return el;
  }

  function clearNotes(shadow) {
    shadow.querySelectorAll('.' + NOTE_CLASS).forEach((el) => el.remove());
  }

  async function renderNote() {
    const state = readState();
    if (!state || state.mode !== 'immediate' || !state.campaignId || !state.note) return;

    const shadow = getShadowRoot();
    if (!shadow) return;

    const cart = await fetchCart();
    if (!cart || !cart.items || !cart.items.length) {
      clearNotes(shadow);
      return;
    }

    const cid = state.campaignId;
    const currency = (cart.currency || '').toUpperCase();
    const threshold = state.thresholds && state.thresholds[currency];
    // Campaign doesn't cover this currency — nothing to communicate.
    if (typeof threshold !== 'number') {
      clearNotes(shadow);
      return;
    }

    // Once the qualifying subtotal (excluding the gift) reaches the threshold the discount unlocks,
    // so the "free when you reach $X" note is no longer relevant.
    const subtotalExGift = cart.items.reduce(
      (sum, item) => (item.properties && item.properties._qr_offer === cid ? sum : sum + item.final_line_price),
      0
    );
    const unlocked = subtotalExGift >= threshold;
    const noteText = state.note.replace(/\{threshold\}/g, formatMoney(threshold));

    // UpCart renders cart items in the same order as /cart.js, so align by index (the pattern used
    // in snippets/trynow-cart-features.liquid). UpCart tags every element with BOTH an
    // `upcart-internal-*` and an `upcart-public-*` class, so either prefix works — the row uses
    // `internal` to match the existing pattern; the child elements use `public`.
    const rows = shadow.querySelectorAll('li.upcart-internal-cart-item');
    const count = Math.min(cart.items.length, rows.length);
    for (let i = 0; i < count; i++) {
      const item = cart.items[i];
      const row = rows[i];
      if (!row) continue;

      const isGift = item.properties && item.properties._qr_offer === cid;
      const existing = row.querySelector('.' + NOTE_CLASS);

      // The gift is a fixed-quantity reward — hide its quantity selector (locked or not). Explicitly
      // restore it on non-gift rows: index alignment means a reused row node could otherwise inherit
      // a previous gift's hidden selector. `important` so it wins over UpCart's own styles.
      const qty = row.querySelector('.upcart-public-component-product-tile__quantity-selector');
      if (qty) {
        if (isGift) qty.style.setProperty('display', 'none', 'important');
        else qty.style.removeProperty('display');
      }

      if (isGift && !unlocked) {
        if (existing) {
          if (existing.textContent !== noteText) existing.textContent = noteText;
        } else {
          const note = makeNoteEl(noteText);
          // Append the note to the end of the tile's controls, falling back to the row if that
          // container isn't found in the current UpCart render.
          const controls = row.querySelector('.upcart-public-component-product-tile__controls');
          if (controls) {
            controls.appendChild(note);
          } else {
            row.appendChild(note);
          }
        }
      } else if (existing) {
        existing.remove();
      }
    }
  }

  let burstTimer = null;
  function scheduleRender() {
    // UpCart can fire several events per change; coalesce a burst into one render cycle instead of
    // fetching /cart.js per event. The second pass re-applies after UpCart's async repaint settles
    // so our note/quantity-hide survive it, without a heavier MutationObserver.
    clearTimeout(burstTimer);
    burstTimer = setTimeout(() => {
      renderNote().catch(() => {});
      setTimeout(() => renderNote().catch(() => {}), 300);
    }, 60);
  }

  UPCART_EVENTS.forEach((name) => window.addEventListener(name, scheduleRender));
})();
